import { atom, map, type MapStore, type ReadableAtom, type WritableAtom } from 'nanostores';
import type { EditorDocument, ScrollPosition } from '~/components/editor/codemirror/CodeMirrorEditor';
import { ActionRunner } from '~/lib/runtime/action-runner';
import type { ActionCallbackData, ArtifactCallbackData } from '~/lib/runtime/message-parser';
import { webcontainer } from '~/lib/webcontainer';
import type { ITerminal } from '~/types/terminal';
import { unreachable } from '~/utils/unreachable';
import { EditorStore } from './editor';
import { FilesStore, type FileMap } from './files';
import { PreviewsStore } from './previews';
import { TerminalStore } from './terminal';
import type { ActionAlert, StoreFileAction } from '~/types/actions';
import JSZip from 'jszip';
import { description } from '~/lib/persistence/useChatHistory';
import { extractRelativePath } from '~/utils/diff';
import fileSaver from 'file-saver';
import { isAssetFile } from '~/utils/fileIgnore';
import { techStackSelectionStore } from '~/lib/stores/techStackSelection';
import { withResolvers } from '~/utils/promises';
import { WORK_DIR } from '~/utils/constants';
import { reactNativeRunnerStore } from './reactNativeRunner';
import { PreviewMessageType } from '@webcontainer/api';

const { saveAs } = fileSaver;

export interface ArtifactState {
  id: string;
  title: string;
  closed: boolean;
  runner: ActionRunner;
  type?: string;
}

export type ArtifactUpdateState = Pick<ArtifactState, 'title' | 'closed'>;

type Artifacts = MapStore<Record<string, ArtifactState>>;

export type WorkbenchViewType = 'code' | 'preview' | 'canvas';
export type DeviceViewType = 'mobile' | 'tablet' | 'desktop';

export class WorkbenchStore {
  #previewsStore = new PreviewsStore(webcontainer);
  #filesStore = new FilesStore(webcontainer);
  #editorStore = new EditorStore(this.#filesStore);
  #terminalStore = new TerminalStore(webcontainer);
  #bugFixerTerminalReady = withResolvers<void>();

  #reloadedMessages = new Set<string>();

  artifacts: Artifacts = import.meta.hot?.data.artifacts ?? map({});

  showWorkbench: WritableAtom<boolean> = import.meta.hot?.data.showWorkbench ?? atom(false);
  currentView: WritableAtom<WorkbenchViewType> = import.meta.hot?.data.currentView ?? atom('preview');
  deviceView: WritableAtom<DeviceViewType> = import.meta.hot?.data.deviceView ?? atom('desktop');
  unsavedFiles: WritableAtom<Set<string>> = import.meta.hot?.data.unsavedFiles ?? atom(new Set<string>());
  bugFixerTerminalOpen: WritableAtom<boolean> = import.meta.hot?.data.bugFixerTerminalOpen ?? atom(false);
  modifiedFiles = new Set<string>();
  artifactIdList: string[] = [];
  actionAlert: WritableAtom<ActionAlert | undefined> =
    import.meta.hot?.data.unsavedFiles ?? atom<ActionAlert | undefined>(undefined);
  actionsSettled: WritableAtom<boolean> = import.meta.hot?.data.actionsSettled ?? atom(true);
  #currentRunnerUnsub?: () => void;

  constructor() {
    if (import.meta.hot) {
      import.meta.hot.data.artifacts = this.artifacts;
      import.meta.hot.data.unsavedFiles = this.unsavedFiles;
      import.meta.hot.data.showWorkbench = this.showWorkbench;
      import.meta.hot.data.currentView = this.currentView;
      import.meta.hot.data.deviceView = this.deviceView;
      import.meta.hot.data.actionAlert = this.actionAlert;
      import.meta.hot.data.actionsSettled = this.actionsSettled;
      import.meta.hot.data.bugFixerTerminalOpen = this.bugFixerTerminalOpen;
    }

    this.#initReactNativeRunner();
  }

  /**
   * Wire the browser-metro RN runner: activate it for `reactnative` projects,
   * feed it the workbench's file map (the single source of truth), and route its
   * bundler/runtime errors into `previewError` so the existing error-fix loop and
   * error UI keep working. See `reactNativeRunner.ts`.
   */
  #initReactNativeRunner() {
    reactNativeRunnerStore.setErrorSink((error) => {
      const previewErrorAtom = this.#previewsStore.previewError;
      previewErrorAtom.set([
        ...previewErrorAtom.get(),
        {
          type: error.source === 'runtime' ? PreviewMessageType.PREVIEW_UNCAUGHT_EXCEPTION : 'TERMINAL_ERROR',
          message: error.message,
          stack: error.stack,
        },
      ]);
    });

    // Activate/deactivate on framework changes (`.subscribe` fires immediately).
    techStackSelectionStore.subscribe((config) => {
      const isReactNative = config.framework === 'reactnative';
      reactNativeRunnerStore.setActive(isReactNative);

      if (isReactNative) {
        reactNativeRunnerStore.setFiles(this.files.get());
      }
    });

    // Feed file changes to the runner (no-op unless it is active).
    this.files.subscribe((files) => {
      reactNativeRunnerStore.setFiles(files);
    });
  }

  get reactNativeRunner() {
    return reactNativeRunnerStore;
  }

  createBugFixerTerminal() {
    this.bugFixerTerminalOpen.set(true);
    return this.#bugFixerTerminalReady.promise;
  }

  destroyBugFixerTerminal() {
    this.bugFixerTerminalOpen.set(false);
    this.#bugFixerTerminalReady = withResolvers<void>();
  }

  destroyDualiteTerminal() {
    this.#terminalStore.destroyDualiteTerminal();
  }

  get dualiteTerminal() {
    return this.#terminalStore.dualiteTerminal;
  }

  get bugFixerTerminal() {
    return this.#terminalStore.bugFixerTerminal;
  }

  attachDualiteTerminal(terminal: ITerminal) {
    this.#terminalStore.attachDualiteTerminal(terminal);
  }

  attachBugFixerTerminal(terminal: ITerminal) {
    this.#terminalStore.attachBugFixerTerminal(terminal);
    this.#bugFixerTerminalReady.resolve();
  }

  get previews() {
    return this.#previewsStore.previews;
  }

  get previewError() {
    return this.#previewsStore.previewError;
  }

  clearError() {
    this.#previewsStore.clearError();

    // Also reset the terminal error state in DualiteShell
    try {
      if (this.dualiteTerminal) {
        this.dualiteTerminal.resetErrorState();
      }
    } catch (error) {
      console.error('Failed to reset terminal error state:', error);
    }
  }

  get files() {
    // console.log('from workbench.ts :', this.#filesStore.files);
    return this.#filesStore.files;
  }

  get currentDocument(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.currentDocument;
  }

  get selectedFile(): ReadableAtom<string | undefined> {
    return this.#editorStore.selectedFile;
  }

  get firstArtifact(): ArtifactState | undefined {
    return this.#getArtifact(this.artifactIdList[0]);
  }

  get firstAIArtifact(): ArtifactState | undefined {
    // const framework = techStackSelectionStore.get().framework;

    // if (framework === 'flutter') {
    //   return this.#getArtifact(this.artifactIdList[0]);
    // } else {
    //   return this.#getArtifact(this.artifactIdList[1]);
    // }

    return this.#getArtifact(this.artifactIdList[1]);
  }

  get filesCount(): number {
    return this.#filesStore.filesCount;
  }

  get showTerminal() {
    return this.#terminalStore.showTerminal;
  }

  toggleTerminal(value?: boolean) {
    this.#terminalStore.toggleTerminal(value);
  }

  attachTerminal(terminal: ITerminal) {
    this.#terminalStore.attachTerminal(terminal);
  }

  onTerminalResize(cols: number, rows: number) {
    this.#terminalStore.onTerminalResize(cols, rows);
  }

  setDocuments(files: FileMap) {
    this.#editorStore.setDocuments(files);

    if (this.#filesStore.filesCount > 0 && this.currentDocument.get() === undefined) {
      // we find the first file and select it
      for (const [filePath, dirent] of Object.entries(files)) {
        if (dirent?.type === 'file') {
          this.setSelectedFile(filePath);
          break;
        }
      }
    }
  }

  setShowWorkbench(show: boolean) {
    this.showWorkbench.set(show);
  }

  setCurrentDocumentContent(newContent: string) {
    const filePath = this.currentDocument.get()?.filePath;

    if (!filePath) {
      return;
    }

    const originalContent = this.#filesStore.getFile(filePath)?.content;
    const normalizedOriginal = originalContent?.replace(/\r\n/g, '\n');
    const unsavedChanges = originalContent !== undefined && normalizedOriginal !== newContent;

    this.#editorStore.updateFile(filePath, newContent);

    const currentDocument = this.currentDocument.get();

    if (currentDocument) {
      const previousUnsavedFiles = this.unsavedFiles.get();

      if (unsavedChanges && previousUnsavedFiles.has(currentDocument.filePath)) {
        return;
      }

      const newUnsavedFiles = new Set(previousUnsavedFiles);

      if (unsavedChanges) {
        newUnsavedFiles.add(currentDocument.filePath);
      } else {
        newUnsavedFiles.delete(currentDocument.filePath);
      }

      this.unsavedFiles.set(newUnsavedFiles);
    }
  }

  setCurrentDocumentScrollPosition(position: ScrollPosition) {
    const editorDocument = this.currentDocument.get();

    if (!editorDocument) {
      return;
    }

    const { filePath } = editorDocument;

    this.#editorStore.updateScrollPosition(filePath, position);
  }

  setSelectedFile(filePath: string | undefined) {
    this.#editorStore.setSelectedFile(filePath);
  }

  async saveFile(filePath: string, storeFileActions: ((fileActions: StoreFileAction[]) => Promise<void>) | undefined) {
    const documents = this.#editorStore.documents.get();
    const document = documents[filePath];

    if (document === undefined) {
      return;
    }

    console.log('[refresh] saveFile workbench:', filePath, document.value);
    await this.#filesStore.saveFile(filePath, document.value);

    const newUnsavedFiles = new Set(this.unsavedFiles.get());
    newUnsavedFiles.delete(filePath);

    this.unsavedFiles.set(newUnsavedFiles);
  }

  async saveFileManually(filePath: string, content: string) {
    await this.#filesStore.saveFile(filePath, content);
  }

  async saveCurrentDocument(storeFileActions: ((fileActions: StoreFileAction[]) => Promise<void>) | undefined) {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    // console.log('[refresh] save current document:', initialFileActions);

    await this.saveFile(currentDocument.filePath, storeFileActions);
  }

  resetCurrentDocument() {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    const { filePath } = currentDocument;
    const file = this.#filesStore.getFile(filePath);

    if (!file) {
      return;
    }

    this.setCurrentDocumentContent(file.content);
  }

  async saveAllFiles(
    initialFileActions: StoreFileAction[],
    storeFileActions: ((fileActions: StoreFileAction[]) => Promise<void>) | undefined,
  ) {
    console.log('[refresh] saveAllFiles: ', this.unsavedFiles.get());

    for (const filePath of this.unsavedFiles.get()) {
      await this.saveFile(filePath, storeFileActions);
    }
  }

  getFileModifcations() {
    return this.#filesStore.getFileModifications();
  }

  getModifiedFiles() {
    return this.#filesStore.getModifiedFiles();
  }

  resetAllFileModifications() {
    this.#filesStore.resetFileModifications();
  }

  abortAllActions() {
    // TODO: what do we wanna do and how do we wanna recover from this?
  }

  addArtifact({ messageId, title, id, type }: ArtifactCallbackData) {
    const artifact = this.#getArtifact(messageId);

    if (artifact) {
      return;
    }

    if (!this.artifactIdList.includes(messageId)) {
      this.artifactIdList.push(messageId);
    }

    const runner = new ActionRunner(
      webcontainer,
      () => this.dualiteTerminal,
      (alert) => {
        if (this.#reloadedMessages.has(messageId)) {
          return;
        }

        this.actionAlert.set(alert);
      },
    );

    this.artifacts.setKey(messageId, {
      id: id || '',
      title: title || '',
      closed: false,
      type,
      runner,
    });

    this.#currentRunnerUnsub?.();
    this.#currentRunnerUnsub = runner.actions.subscribe((actionsMap) => {
      const values = Object.values(actionsMap);
      const settled = values.length === 0 || values.every((a) => a.status !== 'pending');
      this.actionsSettled.set(settled);
    });
  }

  updateArtifact({ messageId }: ArtifactCallbackData, state: Partial<ArtifactUpdateState>) {
    const artifact = this.#getArtifact(messageId);

    if (!artifact) {
      return;
    }

    this.artifacts.setKey(messageId, { ...artifact, ...state });
  }

  async addAction(data: ActionCallbackData) {
    console.log('[api] addAction workbench:', data);

    const { messageId } = data;

    const artifact = this.#getArtifact(messageId);

    if (!artifact) {
      unreachable('Artifact not found');
    }

    artifact.runner.addAction(data);
  }

  async addHistoryAction(data: ActionCallbackData) {
    console.log('[api] addHistoryAction workbench:', data);

    const { messageId } = data;

    const artifact = this.#getArtifact(messageId);

    if (!artifact) {
      unreachable('Artifact not found');
    }

    artifact.runner.addHistoryAction(data);
  }

  async runAction(data: ActionCallbackData) {
    // console.log('[api] runAction workbench:', data);

    const { messageId } = data;

    const artifact = this.#getArtifact(messageId);

    if (!artifact) {
      unreachable('Artifact not found');
    }

    artifact.runner.runAction(data);
  }

  #getArtifact(id: string) {
    const artifacts = this.artifacts.get();
    return artifacts[id];
  }
  async getAllFiles() {
    const files = this.files.get();
    const fileActions: StoreFileAction[] = [];

    for (const [filePath, dirent] of Object.entries(files)) {
      if (dirent?.type === 'file') {
        // remove /home/project/ from starting of the filePath
        const relativePath = filePath.slice(WORK_DIR.length + 1);
        fileActions.push({ type: 'file', filePath: relativePath, content: dirent.content, id: relativePath });
      }
    }

    return fileActions;
  }

  async getAllFilesMap() {
    const files = this.files.get();
    return files;
  }

  async getZipBlob() {
    const zip = new JSZip();
    const { framework, language } = techStackSelectionStore.get();
    console.log('framework', framework, 'language', language);

    const files = this.files.get();

    for (const [filePath, dirent] of Object.entries(files)) {
      if (dirent?.type === 'file') {
        if (
          filePath.includes('yarn.lock') ||
          filePath.includes('package-lock.json') ||
          filePath.includes('pnpm-lock.yaml') ||
          filePath.includes('bun.lockb') ||
          filePath.includes('LICENSE')
        ) {
          continue; // Skip yarn.lock file since it's conflicting with Netlify Setup.
        }

        const relativePath = extractRelativePath(filePath);
        const pathSegments = relativePath.split('/');

        let currentFolder = zip;

        if (pathSegments.length > 1) {
          for (let i = 0; i < pathSegments.length - 1; i++) {
            currentFolder = currentFolder.folder(pathSegments[i])!;
          }
        }

        const fileName = pathSegments[pathSegments.length - 1];
        console.log('fileName >>>>>>', fileName, isAssetFile(fileName), dirent.content);

        // Handle binary vs text content appropriately
        if (isAssetFile(fileName)) {
          currentFolder.file(fileName, dirent.content, { base64: true });
        } else {
          currentFolder.file(fileName, dirent.content);
        }
      }
    }

    let netlifyConfig = '';

    /*
     * Configure Netlify settings based on framework
     *  if (framework.includes('shadcn')) {
     *    netlifyConfig = `[build]
     * command = "npm install --legacy-peer-deps && npm run build"
     * publish = "dist"
     * base = "."  # Changed from "/" to "."
     *
     * [build.environment]
     * NODE_VERSION = "18"
     * CI = "false"
     * NETLIFY_BUILD_SKIP = "false"
     * VITE_CJS_IGNORE_WARNING = "true"
     * SKIP_PREFLIGHT_CHECK = "true"
     * NODE_OPTIONS = "--max_old_space_size=4096"
     * NPM_FLAGS = "--legacy-peer-deps --no-audit"
     *
     * [build.processing]
     * skip_processing = false
     *
     * [[redirects]]
     * from = "/*"
     * to = "/index.html"
     * status = 200
     * force = true`;
     *  }
     *  else if (framework.includes('angular')) {
     *    netlifyConfig = `[build]
     * command = "npm install && ng build --configuration=production"
     * publish = "dist/demo"
     *
     * [[redirects]]
     * from = "/*"
     * to = "/index.html"
     * status = 200
     * force = true
     *
     * [build.environment]
     * NODE_VERSION = "18"
     * NPM_FLAGS = "--legacy-peer-deps"
     *
     * [dev]
     * command = "ng serve"
     * port = 4200
     * targetPort = 4200
     * `;
     *  }
     */

    // HTML + Tailwind (Vite, JS): Tailwind is compiled at build time; publish `dist`, not repo root.
    if (framework.includes('html_tailwind') && language === 'js') {
      netlifyConfig = `[build]
  command = "npm install && npm run build"
  publish = "dist"
`;
    }

    if (framework.includes('react') || framework.includes('vue')) {
      netlifyConfig = `[build]
    command = "npm install && npm run build"
  publish = "dist"
  base = "/"
  ignore = "false"  # Never skip build
  force = "true"    # Force build even if no changes detected

[build.environment]
  NODE_VERSION = "18"
  CI = "true"       # Ensure we're in CI mode
  NETLIFY_BUILD_SKIP = "false"  # Never skip build

[build.processing]
  skip_processing = false  # Ensure processing is not skipped

[dev]
  command = "vite"
  port = 3000
  publish = "dist"`;
    }

    if (framework.includes('next')) {
      /*
       * For Next.js 13.5+ on Netlify, zero configuration is needed.
       * The OpenNext adapter automatically handles the build process.
       */
      netlifyConfig = `
      [build]
  command = "npm run build"
  publish = ".next"
  base = "."

      [[plugins]]
      package = "@netlify/plugin-nextjs"
      `;
    }

    // Add netlify.toml to zip
    zip.file('netlify.toml', netlifyConfig);

    const readmeContent = `
    # Project Setup
    
    To run this project, follow these steps:
    
    1. Extract the zip file.
    2. Run \`npm install\` to install dependencies.
    3. Run \`npm run dev\` to start the development server.
    
    This project was generated through Alpha. For more information, visit [dualite.dev](https://dualite.dev).
        `;
    zip.file('README.md', readmeContent.trim());

    return await zip.generateAsync({ type: 'blob' });
  }

  async downloadZip(fileName?: string) {
    // Get the project name from the description input, or use a default name
    const projectName = (description.value ?? 'project').toLocaleLowerCase().split(' ').join('_');
    const timestampHash = Date.now().toString(36).slice(-6);
    const uniqueProjectName = fileName || `${projectName}_${timestampHash}_dualiteproject`;

    // Get the zip blob and save it
    const content = await this.getZipBlob();
    saveAs(content, `${uniqueProjectName}.zip`);
  }

  setDeviceView(view: DeviceViewType) {
    this.deviceView.set(view);
  }

  addAllActions(
    actions: any[],
    messageId: string,
    artifactId: string,
    artifactTitle: string,
    waitForCompletion = false,
  ): Promise<void> | void {
    this.addArtifact({ messageId, id: artifactId, title: artifactTitle, type: 'artifact' });

    for (const [index, action] of actions.entries()) {
      if ('type' in action) {
        const actionData: ActionCallbackData = {
          artifactId,
          messageId,
          actionId: `action-${Date.now()}-${index}`,
          action: {
            type: action.type,
            filePath: action.id,
            content: action.content,
          },
        };
        this.addAction(actionData);
        this.runAction(actionData);
      }
    }

    if (waitForCompletion) {
      const artifact = this.#getArtifact(messageId);

      return (artifact?.runner.waitForCompletion() ?? Promise.resolve()).then(() =>
        this.#waitForRestoredFileSnapshot(actions),
      );
    }

    return undefined;
  }

  /**
   * WebContainer file writes complete before FilesStore's buffered watcher has
   * published them. Browser Metro consumes FilesStore, so RN restoration must
   * wait for this second boundary rather than only the action queue.
   */
  #waitForRestoredFileSnapshot(actions: any[]): Promise<void> {
    const expectedPaths = actions
      .filter((action) => action?.type === 'file' && !action.deleted)
      .map((action) => String(action.id || action.filePath || ''))
      .filter(Boolean)
      .map((path) => (path.startsWith(WORK_DIR) ? path : `${WORK_DIR}/${path.replace(/^\/+/, '')}`));

    if (expectedPaths.length === 0) {
      return Promise.resolve();
    }

    const snapshotComplete = (files: FileMap) => expectedPaths.every((path) => files[path]?.type === 'file');

    if (snapshotComplete(this.files.get())) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let settled = false;
      const subscription: { unsubscribe?: () => void } = {};

      const finish = () => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeout);
        subscription.unsubscribe?.();
        resolve();
      };

      const timeout = setTimeout(() => {
        const files = this.files.get();
        const missing = expectedPaths.filter((path) => files[path]?.type !== 'file');

        console.warn('[rn-runner] timed out waiting for restored file snapshot', missing);
        finish();
      }, 10_000);

      subscription.unsubscribe = this.files.listen((files) => {
        if (snapshotComplete(files)) {
          finish();
        }
      });

      // Cover a watcher update occurring between the pre-check and listener.
      if (snapshotComplete(this.files.get())) {
        finish();
      }
    });
  }
}

export const workbenchStore = new WorkbenchStore();

export function hasFileActionsInWorkbench(messageId: string): boolean {
  try {
    const artifacts = workbenchStore.artifacts.get();
    const artifact = artifacts[messageId];

    if (!artifact || !artifact.runner) {
      return false;
    }

    const actions = artifact.runner.actions.get();
    return Object.values(actions).some((action) => action.type === 'file');
  } catch (error) {
    console.error('[Workbench] Error checking file actions in workbench:', error);
    return false;
  }
}
